export const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, "0");

/** "18:42" in the viewer's local time. */
export function clock(iso: string | number): string {
  const date = new Date(iso);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "02:12:03" in the viewer's local time: failures a few seconds apart must stay apart. */
export function clockSeconds(iso: string | number): string {
  const date = new Date(iso);
  return `${clock(iso)}:${pad(date.getSeconds())}`;
}

/** "25 Oct 18:42" in the viewer's local time and language. */
export function dayClock(iso: string | number): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${day} ${clock(iso)}`;
}

/** Running durations: "45 s", "12 min 05 s", "3 h 5 min", "2 d 4 h". */
export function duration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${pad(s % 60)} s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** "just now", "12 s ago", "3 min ago", "2 h ago", "4 d ago". */
export function ago(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "a · b  +3 more" */
export function capList(items: string[], max: number): string {
  if (items.length <= max) return items.join(" · ");
  return `${items.slice(0, max).join(" · ")}  +${items.length - max} more`;
}

export function timeZoneName(): string {
  try {
    return (
      new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    return "";
  }
}

/** "980 B", "6.3 MB", "443 GB": decimal units, like the backup tools report them. */
export function formatBytes(bytes: number): string {
  const units = ["B", "kB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${value.toFixed(unit === 0 || value >= 10 ? 0 : 1)} ${units[unit]}`;
}
